import { api } from '@packages/backend/convex/_generated/api';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useMatches,
  useRouter,
} from '@tanstack/react-router';
import { signOut } from 'aws-amplify/auth';
import { useQuery } from 'convex-helpers/react';
import { UserCircle } from 'lucide-react';
import { ErrorAlert } from '@/components/ErrorAlert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { ActionButton } from '@/components/ui+/ActionButton';
import { BreadcrumbProvider, Breadcrumbs } from '@/components/ui+/breadcrumbs';
import { ModeToggle } from '@/components/ui+/ModeToggle';
import { links } from '@/routes/admin/links';
import { cn } from '@/utils/cn';

export const Route = createFileRoute('/a')({
  beforeLoad: ({ context, location }) => {
    // Keep the current URL for signed-out users so they can log in and land on the page they asked for.
    if (!context.isAuthenticated) {
      return;
    }

    // If they are logged in, but a restaurant user, redirect restaurant root page.
    // This will then redirect them to their restaurant page.
    if (context.convexUser.userType === 'restaurantUser') {
      throw redirect({ to: '/r' });
    }

    // Go to default page for admin
    if (location.pathname === '/a') {
      throw redirect({ to: '/a/restaurants' });
    }
  },
  component: Component,
});

function Component() {
  const router = useRouter();
  const { pathname } = useLocation();
  const noPagePadding = useMatches().some((match) => match.staticData.noPagePadding);

  // Note: It's important to keep these queries here as it allows Convex provider to cache
  // the queries for nested child routes.
  const ordersQuery = useQuery(api.orders.listRecent);
  const standalonePrintersQuery = useQuery(api.standalonePrinters.list);
  const hasPendingMigrationsQuery = useQuery(api.docMigrations.hasPending);

  if (ordersQuery.isError) {
    return <ErrorAlert message={ordersQuery.error.message} title="Error loading orders" />;
  }
  if (standalonePrintersQuery.isError) {
    return (
      <ErrorAlert
        message={standalonePrintersQuery.error.message}
        title="Error loading standalonePrintersQuery"
      />
    );
  }

  const handleSignOut = async () => {
    await signOut();
    await router.invalidate();
  };

  return (
    <SidebarProvider defaultOpen={true}>
      <BreadcrumbProvider>
        <Sidebar>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <Link to={'/'}>
                  <span className="text-orange-500 dark:text-orange-400">Super Admin</span>
                </Link>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {links.map((navLink) => (
                    <SidebarMenuItem key={navLink.link.to}>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          pathname === navLink.link.to || pathname.startsWith(`${navLink.link.to}/`)
                        }
                      >
                        <Link to={navLink.link.to}>
                          {!!navLink.icon && <navLink.icon />}
                          <span>{navLink.name}</span>
                          {hasPendingMigrationsQuery.data &&
                            navLink.link.to === '/a/doc-migrations' && (
                              <SidebarMenuBadge>
                                <span className="size-2 rounded-full bg-red-500" />
                              </SidebarMenuBadge>
                            )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
        <SidebarInset>
          <header className="flex h-14 lg:h-[60px] items-center gap-4 border-b bg-muted/40 px-4 lg:px-6">
            <SidebarTrigger />
            <Breadcrumbs />
            <div className="w-full flex-1" />
            <ModeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton className="rounded-full" size="icon" variant="secondary">
                  <UserCircle className="h-5 w-5" />
                  <span className="sr-only">Toggle user menu</span>
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>Settings</DropdownMenuItem>
                <DropdownMenuItem>Support</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          <main
            className={cn('flex flex-1 flex-col', !noPagePadding && 'gap-4 p-4 lg:gap-6 lg:p-6')}
          >
            <Outlet />
          </main>
        </SidebarInset>
      </BreadcrumbProvider>
    </SidebarProvider>
  );
}
